import {
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
  type Table_Internal,
  type TableFeatures,
} from '@tanstack/react-table';

/**
 * Creates a custom pagination row model.
 *
 * @returns A function that takes a table instance and returns a memoed function to get the paginated row model.
 */
export function createPaginatedRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(): (table: Table<TFeatures, TData>) => () => RowModel<TFeatures, TData> {
  return (_table) => {
    const table = _table as Table_Internal<TFeatures, TData>;
    return tableMemo({
      feature: 'rowPaginationFeature',
      table,
      fnName: 'table.getPaginatedRowModel',
      memoDeps: () => [
        table.getPrePaginatedRowModel(),
        table.atoms.pagination?.get(),
        table.options.paginateExpandedRows
          ? table.atoms.expanded?.get()
          : undefined,
      ],
      fn: () => _createPaginatedRowModel(table),
    });
  };
}

/**
 * Creates a custom pagination row model.
 */
function _createPaginatedRowModel<
  TFeatures extends TableFeatures,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends RowData = any,
>(table: Table_Internal<TFeatures, TData>): RowModel<TFeatures, TData> {
  const prePaginatedRowModel = table.getPrePaginatedRowModel();
  const pagination = table.atoms.pagination?.get();

  if (
    !prePaginatedRowModel.rows.length ||
    table.options.enablePagination !== true
  ) {
    return prePaginatedRowModel;
  }

  const { pageSize, pageIndex } = pagination;
  const { rowsById } = prePaginatedRowModel;
  const pageStart = pageSize * pageIndex;
  const pageEnd = pageStart + pageSize;

  const rows = prePaginatedRowModel.rows.slice(pageStart, pageEnd);

  const paginatedRowModel: RowModel<TFeatures, TData> = {
    rows,
    flatRows: [],
    rowsById,
  };

  // keep track of the already added flatRows, to avoid duplication of already expanded sub-rows
  const addedFlatRowsSet = new Set();

  const handleRow = (row: Row<TFeatures, TData>) => {
    if (addedFlatRowsSet.has(row.id)) {
      return;
    }

    paginatedRowModel.flatRows.push(row);
    addedFlatRowsSet.add(row.id);

    if (row.subRows.length) {
      row.subRows.forEach(handleRow);
    }
  };

  paginatedRowModel.rows.forEach(handleRow);

  return paginatedRowModel;
}
